/**
 * B6 (vague « Fluidité ») — Questions / réponses de l'appel d'offres, côté entreprise.
 *
 * L'entreprise pose sa question depuis son écran d'appel d'offres (connectée par son
 * compte ou par le lien reçu par e-mail). L'architecte répond une fois ; la réponse est
 * communiquée à TOUTES les entreprises consultées, sans le nom de celle qui a demandé.
 * Source : GET/POST /api/entreprise/consultations/{invitation_id}/questions.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChatCircleText, PaperPlaneTilt } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return ''; }
};

export default function ConsultationQuestions({ invitationId, lots = [], onCount }) {
  const [questions, setQuestions] = useState([]);
  const [peutPoser, setPeutPoser] = useState(false);
  const [question, setQuestion] = useState('');
  const [lotId, setLotId] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [charge, setCharge] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/entreprise/consultations/${invitationId}/questions`, { withCredentials: true });
      setQuestions(data.questions || []);
      setPeutPoser(!!data.peut_poser);
      onCount?.((data.questions || []).length);
    } catch {
      setQuestions([]);
    } finally { setCharge(true); }
  }, [invitationId, onCount]);

  useEffect(() => { load(); }, [load]);

  const poser = async () => {
    if (!question.trim()) return;
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/entreprise/consultations/${invitationId}/questions`,
        { question, lot_id: lotId || null }, { withCredentials: true });
      toast.success(data.message);
      setQuestion('');
      setLotId('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "La question n'a pas été envoyée");
    } finally { setEnvoi(false); }
  };

  return (
    <div className="space-y-4" data-testid="consultation-questions">
      <div className="border border-[#E0E0DB] bg-[#FAFAF8] px-4 py-3 text-sm text-[#2C2C2C]">
        Une question sur l'appel d'offres ? Posez-la ici : l'architecte répond une fois, et sa réponse est
        communiquée à <strong>toutes les entreprises consultées</strong>, sans le nom de votre entreprise.
      </div>

      {peutPoser ? (
        <div className="border border-[#E0E0DB] bg-white p-4 space-y-2" data-testid="consultation-question-form">
          {lots.length > 1 && (
            <select value={lotId} onChange={(e) => setLotId(e.target.value)} aria-label="Lot concerné"
              className="border border-[#E0E0DB] px-2 py-1 text-sm" data-testid="consultation-question-lot">
              <option value="">Tous les lots</option>
              {lots.map((l) => <option key={l.id} value={l.id}>{l.libelle || l.nom}</option>)}
            </select>
          )}
          <Textarea rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} className="rounded-none"
            placeholder="Votre question (précisez le poste ou la pièce du dossier concernés)" data-testid="consultation-question-texte" />
          <div className="flex justify-end">
            <Button onClick={poser} disabled={envoi || !question.trim()} className="rounded-none gap-2 bg-[#C75B39] hover:bg-[#D97B5C]"
              data-testid="consultation-question-envoyer">
              <PaperPlaneTilt size={14} /> Poser la question
            </Button>
          </div>
        </div>
      ) : charge && (
        <p className="text-sm text-[#6B6B6B]" data-testid="consultation-questions-closes">
          Cet appel d'offres n'accepte plus de questions (date limite passée ou consultation terminée).
        </p>
      )}

      {charge && questions.length === 0 && (
        <p className="text-sm text-[#9A9A9A]">Aucune question pour l'instant.</p>
      )}
      <ol className="space-y-3" data-testid="consultation-questions-liste">
        {questions.map((q) => (
          <li key={q.id} className="border border-[#E0E0DB] bg-white p-4 text-sm">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <ChatCircleText size={16} className="text-[#1E3A5F]" />
              <span className="font-semibold text-[#1E3A5F]">Question {q.numero}</span>
              {q.lots?.length > 0 && <span className="text-xs text-[#6B6B6B]">{q.lots.join(' · ')}</span>}
              {q.de_moi && <span className="text-[10px] uppercase font-bold text-[#C75B39]">Votre question</span>}
              <span className="text-xs text-[#9A9A9A] ml-auto">{fmtDate(q.posee_le)}</span>
            </div>
            <p className="text-[#2C2C2C] whitespace-pre-wrap">{q.question}</p>
            {q.reponse ? (
              <div className="mt-2 border-l-2 border-[#2D6A4F] pl-3" data-testid={`consultation-reponse-${q.numero}`}>
                <p className="text-xs font-semibold text-[#2D6A4F]">Réponse de l'architecte, le {fmtDate(q.repondu_le)}</p>
                <p className="text-[#2C2C2C] whitespace-pre-wrap">{q.reponse}</p>
              </div>
            ) : (
              <p className="mt-2 text-xs text-[#D4880F]">En attente de réponse</p>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
